import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PermissionsContext = createContext({
    permissions: [], role: '', can: () => false,
    orgRole: 'member', pillarRoles: {},
    hasPillarAccess: () => true, isReadOnly: () => false,
    canInPillar: () => false, isOrgAdmin: () => false,
});

export function PermissionsProvider({ children }) {
    const { user, getAuthHeader } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [role, setRole] = useState('');
    const [orgRole, setOrgRole] = useState('member');
    const [pillarRoles, setPillarRoles] = useState({});

    useEffect(() => {
        if (!user) {
            setPermissions([]); setRole(''); setOrgRole('member'); setPillarRoles({});
            return;
        }
        axios.get(`${API}/auth/permissions`, { headers: getAuthHeader() })
            .then(res => {
                setPermissions(res.data.permissions || []);
                setRole(res.data.role || '');
                setOrgRole(res.data.org_role || 'member');
                setPillarRoles(res.data.pillar_roles || {});
            })
            .catch(() => { setPermissions([]); setRole(''); setOrgRole('member'); setPillarRoles({}); });
    }, [user, getAuthHeader]);

    // Backward-compat flat permission check
    const can = useCallback((permission) => {
        if (role === 'super_admin') return true;
        return permissions.includes(permission);
    }, [permissions, role]);

    // Is user an org-level admin?
    const isOrgAdmin = useCallback(() => {
        if (user?.is_super_admin) return true;
        return orgRole === 'org_admin' || user?.is_admin;
    }, [orgRole, user]);

    // Does user have any role in a specific pillar?
    const hasPillarAccess = useCallback((pillarId) => {
        if (!pillarId) return true;
        if (user?.is_super_admin) return true;
        if (orgRole === 'org_admin' || user?.is_admin) return true;
        // No pillar_roles set = legacy user, show all pillars
        if (!pillarRoles || Object.keys(pillarRoles).length === 0) return true;
        return !!pillarRoles[pillarId];
    }, [pillarRoles, orgRole, user]);

    // Is user read-only in a specific pillar?
    const isReadOnly = useCallback((pillarId) => {
        if (!pillarId) return false;
        if (user?.is_super_admin) return false;
        if (orgRole === 'org_admin' || user?.is_admin) return false;
        if (!pillarRoles || Object.keys(pillarRoles).length === 0) return false;
        const pr = pillarRoles[pillarId];
        if (!pr) return true; // no access = read-only equivalent
        return pr.read_only || false;
    }, [pillarRoles, orgRole, user]);

    // Check a specific permission within a pillar
    const canInPillar = useCallback((pillarId, permission) => {
        if (user?.is_super_admin) return true;
        if (orgRole === 'org_admin' || user?.is_admin) return true;
        if (!pillarRoles || Object.keys(pillarRoles).length === 0) {
            // Legacy user, use flat permissions
            return permissions.includes(permission);
        }
        const pr = pillarRoles[pillarId];
        if (!pr) return false;
        return (pr.permissions || []).includes(permission);
    }, [pillarRoles, orgRole, user, permissions]);

    return (
        <PermissionsContext.Provider value={{
            permissions, role, can,
            orgRole, pillarRoles,
            hasPillarAccess, isReadOnly, canInPillar, isOrgAdmin,
        }}>
            {children}
        </PermissionsContext.Provider>
    );
}

export function usePermissions() {
    return useContext(PermissionsContext);
}
